import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useAccess, useAccountId } from "../Auth/auth-functions";
import { AzureADRoles } from "../Auth/auth-roles";
import { api } from "./api";
import { AssetDto, ListAssetDto, MutableAssetDto } from "../models/assets.models";

export const useGetAssetsQuery = () => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['assets', accountId],
		queryFn: async () => {
			let res = await api.get<ListAssetDto[]>('/assets');
			return res.data;
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess
	});
};

export const useGetAssetByIdQuery = (assetId: string | null) => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['asset', accountId, assetId],
		queryFn: async () => {
			let res = await api.get<AssetDto>(`/assets/${assetId}`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && assetId !== "" && assetId !== "new" && assetId !== null
	});
};

export const useGetAssetChildrenQuery = (assetId: string | null) => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['assetChildren', accountId, assetId],
		queryFn: async () => {
			let res = await api.get<ListAssetDto[]>(`/assets/${assetId}/children`);
			return res.data;
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && assetId !== "" && assetId !== "new" && assetId !== null
	});
};

export const useGetMakesQuery = () => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['makes', accountId],
		queryFn: async () => {
			let res = await api.get<string[]>('/assets/makes');
			return res.data;
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess
	});
};

export const useGetModelsQuery = () => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['models', accountId],
		queryFn: async () => {
			let res = await api.get<string[]>('/assets/models');
			return res.data;
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess
	});
};

export const useGetAssetTypesQuery = () => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['assetTypes', accountId],
		queryFn: async () => {
			let res = await api.get<string[]>('/assets/types');
			return res.data;
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess
	});
};

export const useGetAssetQRCodeQuery = (assetId: string | null, enabled: boolean = false) => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['assetQRCode', accountId, assetId],
		queryFn: async () => {
			let res = await api.get(`/assets/${assetId}/qrcode`, {
				responseType: 'blob'
			});
			return res.data as Blob;
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && enabled && assetId !== null && assetId !== "" && assetId !== "new"
	});
};

export const useResetAssetQRCodeQuery = (assetId: string | null) => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	const reset = () => queryClient.removeQueries({ queryKey: ['assetQRCode', accountId, assetId], exact: true });
	return { reset };
}

export const useCreateAssetMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<AssetDto>, Error, { asset: MutableAssetDto; files?: File[] }>({
		mutationFn: (options) => {
			const { asset, files } = options;
			if (files && files.length > 0) {
				const formData = new FormData();
				formData.append('asset', JSON.stringify(asset));

				files.forEach((file) => {
					formData.append('files', file);
				});
				return api.post('/assets/files', formData, {
					headers: { 'Content-Type': 'multipart/form-data' }
				});
			}
			return api.post('/assets', asset);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['assets', accountId] });
			queryClient.invalidateQueries({ queryKey: ['asset', accountId] });
		}
	});
};

export const useUpdateAssetMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<AssetDto>, Error, { asset: MutableAssetDto; files?: File[] }>({
		mutationFn: (options) => {
			const { asset, files } = options;
			if (files && files.length > 0) {
				const formData = new FormData();
				formData.append('asset', JSON.stringify(asset));
				files.forEach((file) => {
					formData.append('files', file);
				});
				return api.put(`/assets/${asset.AssetId}/files`, formData, {
					headers: { 'Content-Type': 'multipart/form-data' }
				});
			}
			return api.put(`/assets/${asset.AssetId}`, asset);
		},
		onSuccess: (_data, options) => {
			queryClient.invalidateQueries({ queryKey: ['asset', accountId, options.asset.AssetId] });
			queryClient.invalidateQueries({ queryKey: ['assets', accountId] });
		}
	});
};

export const useDeleteAssetMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<unknown, Error, AssetDto>({
		mutationFn: (asset) => {
			return api.delete(`/assets/${asset.AssetId}`);
		},
		onSuccess: (_data, asset) => {
			queryClient.invalidateQueries({ queryKey: ['asset', accountId, asset.AssetId] });
			queryClient.invalidateQueries({ queryKey: ['assets', accountId] });
		}
	});
};
